import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import type {
  CellEditingStoppedEvent, CellValueChangedEvent, ColDef, ColumnMovedEvent, ColumnResizedEvent, ColumnState, ColumnVisibleEvent,
  FilterChangedEvent, GetRowIdParams, ICellRendererParams, IRowNode, PaginationChangedEvent, RowClassParams, RowClickedEvent,
  RowSelectionOptions, SelectionChangedEvent, SortChangedEvent,
} from 'ag-grid-community';
import { GROUP_AUTO_COLUMN_ID, themeMaterial } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { sortJsonKeysForComparison } from '@/helpers/utils';
import { getColumnFilterType, MultiSelectionColumnConfig, OptionsCustomColumnConfig, TableListEditableNewProps, UsersSelectionColumnConfig } from '@/types/TableListEditable';
import { appTheme } from '@/utils/componentStyles';
import { buildMultiSelectionCellRenderer, buildOptionsCustomCellRenderer, buildUsersSelectionCellRenderer } from './BuildColumns/BuildColumns';
import ColumnVisibilityMenu from './ColumnVisibilityMenu/ColumnVisibilityMenu';
import SelectionHeader from './SelectionHeader/SelectionHeader';
import { ALIGN, ALIGN_TO_JUSTIFY, DEFAULT_FILTERS, TABLE_ROW_SELECTION, CELL_EDITOR_TYPES, ACTIONS_TRIGGERED_BY_USER, MULTIPLE_SELECTION_OPTIONS, PAGE_SIZES } from './TableListEditableConstants';
import type { AlignValue } from './TableListEditableConstants';
import TextAreaCellEditor from './TextAreaCellEditor';
import styles from './TableListEditableNew.module.scss';

const gridTheme = themeMaterial.withParams({ accentColor: appTheme.cartTheme.primaryColor });

export { ALIGN, TABLE_ROW_SELECTION };
export type { AlignValue };

const GRID_MODULES = [AllEnterpriseModule];

const DEFAULT_EMPTY_COLUMNS = [];
const autoHeight = 'autoHeight';
const normal = 'normal';

const serializeComparable = <T extends object>(value: T) => JSON.stringify(value);

const isSameSerializedValue = <T extends object>(left: T, right: T) => (
  serializeComparable(left) === serializeComparable(right)
);

const getCellEditorConfig = (col: TableListEditableNewProps<Record<string, unknown>>['columns'][number]) => {
  if (!col.cellEditor) return {};

  if (col.cellEditor === CELL_EDITOR_TYPES.LARGE_TEXT) {
    return {
      cellEditor: TextAreaCellEditor,
      cellEditorParams: { maxLength: (col.cellEditorParams as any)?.maxLength },
    };
  }

  return { cellEditor: col.cellEditor, cellEditorParams: col.cellEditorParams };
};

export default function TableListEditableNew<RowData extends { onClick?: () => void } & Record<string, unknown>>(
  props: TableListEditableNewProps<RowData>,
) {
  const {
    list,
    columns,
    pageSizes = PAGE_SIZES,
    columnSorting,
    onSortModelChange,
    filteringStateColumns,
    isFiltering,
    filterModel,
    onFilterModelChange,
    loading = false,
    hiddenColumns = DEFAULT_EMPTY_COLUMNS,
    hasCheckbox,
    columnId,
    selection,
    rowSelectionMode = TABLE_ROW_SELECTION.MULTIPLE,
    onSelectionChange = () => { },
    onCellValueChanged,
    isPaginated = true,
    onPageSizeChange,
    onPageChange,
    tableHeight,
    showColumnVisibilityButton = false,
    resetFilters,
    usersColumns = DEFAULT_EMPTY_COLUMNS,
    usersColumnsContent = DEFAULT_EMPTY_COLUMNS,
    hasMinWidth = false,
    treeDataConfig,
    validationStatus,
    expandedRows,
    isRowSelectable: isRowSelectableProp,
    allowColumnMove,
    animateRows = true,
    onColumnWidthsChange,
    onColumnsOrderChanged,
    onColumnVisibilityChanged,
    onResetColumnsSettings,
  } = props;

  const gridRef = useRef<AgGridReact<RowData>>(null);
  const isSyncingSelectionRef = useRef(false);
  const lastEmittedFilterModelRef = useRef<string>('');
  const lastEmittedSortModelRef = useRef<string>('');
  const lastEmittedPageSizeRef = useRef<number | undefined>(undefined);
  const lastEmittedPageIndexRef = useRef<number | undefined>(undefined);
  const [gridReady, setGridReady] = useState(false);
  const paginationPageSizeSelector = useMemo(() => pageSizes, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { t } = useTranslation();
  const columnWidthsRef = useRef<Record<string, number>>({});

  const popupParent = useMemo(() => (typeof document !== 'undefined' ? document.body : null), []);

  const overlayComponentParams = useMemo(() => ({
    loading: { overlayText: t('loading') },
    noRows: { overlayText: t('noResults') },
    noMatchingRows: { overlayText: t('noResults') },
  }), [t]);

  const isRowSelectable = useCallback((node: IRowNode<RowData>) => {
    if (isRowSelectableProp) return isRowSelectableProp(node);

    if (!treeDataConfig?.childrenNotSelectable) return true;

    return (node.data as Record<string, unknown>)?.isChild !== true;
  }, [isRowSelectableProp, treeDataConfig?.childrenNotSelectable]);

  const rowSelection = useMemo<RowSelectionOptions>(() => ({
    mode: rowSelectionMode,
    checkboxes: true,
    checkboxLocation: 'selectionColumn',
    headerCheckbox: false,
    hideDisabledCheckboxes: Boolean(treeDataConfig?.childrenNotSelectable),
    isRowSelectable,
    ...(rowSelectionMode === TABLE_ROW_SELECTION.MULTIPLE && { selectAll: MULTIPLE_SELECTION_OPTIONS.FILTERED }),
  }), [rowSelectionMode, treeDataConfig?.childrenNotSelectable, isRowSelectable]);

  const selectionColumnDef = useMemo<ColDef>(() => ({
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    headerName: '',
    width: treeDataConfig ? 92 : 42,
    minWidth: treeDataConfig ? 92 : 42,
    maxWidth: treeDataConfig ? 92 : 42,
    resizable: false,
    headerComponent: SelectionHeader,
    headerComponentParams: {
      showTreeToggle: Boolean(treeDataConfig),
      showSelectAll: rowSelectionMode === TABLE_ROW_SELECTION.MULTIPLE,
      selectAllMode: MULTIPLE_SELECTION_OPTIONS.FILTERED,
    },
    headerCheckboxSelection: false,
    headerClass: styles.SelectionHeaderCell,
    headerStyle: { padding: 0, overflow: 'hidden' },
    cellClass: (params) => {
      const isSubCR = (params.data as Record<string, unknown> | undefined)?.isSubCRChildren;

      return isSubCR ? `${styles.SelectionCell} ${styles.SubCRSelectionCell}` : styles.SelectionCell;
    },
    cellStyle: { justifyContent: 'center', padding: 0, overflow: 'hidden' },
  }), [treeDataConfig, rowSelectionMode]);

  const filteringEnabledByColumn = useMemo(() => {
    const map = new Map<string, boolean>();
    filteringStateColumns?.forEach((ext) => {
      map.set(ext.columnName, !!ext.filteringEnabled);
    });

    return map;
  }, [filteringStateColumns]);

  const isTreeData = Boolean(treeDataConfig);

  const isFilteringEnabledForColumn = useCallback((columnName?: string) => {
    if (!columnName) return true;

    return filteringEnabledByColumn.has(columnName)
      ? filteringEnabledByColumn.get(columnName) === true
      : true;
  }, [filteringEnabledByColumn]);

  const autoGroupColumnDef = useMemo<ColDef<RowData> | undefined>(() => {
    if (!treeDataConfig) return undefined;
    const expandedCol = columns.find((column) => column.name === treeDataConfig.columnExpanded);
    const isExpandedFilteringEnabled = isFilteringEnabledForColumn(expandedCol?.name);

    return {
      headerName: expandedCol?.title ?? '',
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        suppressCount: true,
        ...(treeDataConfig.innerCellRenderer ? { innerRenderer: treeDataConfig.innerCellRenderer } : {}),
      },
      valueGetter: (params) => {
        const row = params.data as Record<string, unknown> | undefined;
        const primary = row?.[treeDataConfig.columnExpanded];
        const value = primary ?? row?.campaign ?? row?.blockingLocations ?? '';

        return String(value);
      },
      filter: isFiltering && isExpandedFilteringEnabled && expandedCol
        ? getColumnFilterType(expandedCol)
        : false,
      filterParams: {
        filterOptions: expandedCol?.filters || DEFAULT_FILTERS,
        ...(expandedCol?.valueFormatter ? { valueFormatter: expandedCol.valueFormatter } : {}),
      },
      floatingFilter: isFiltering && isExpandedFilteringEnabled,
      floatingFilterComponentParams: { filterPlaceholder: t('dialog.filter') },
      suppressHeaderMenuButton: true,
      width: expandedCol?.width,
      minWidth: expandedCol?.minWidth ?? 150,
      maxWidth: expandedCol?.maxWidth,
      flex: 1,
      resizable: true,
    };
  }, [treeDataConfig, columns, isFiltering, t, isFilteringEnabledForColumn]);

  const resolvedHiddenColumns = useMemo(() => {
    const hidden = new Set(hiddenColumns);

    if (treeDataConfig?.columnExpanded) {
      hidden.add(treeDataConfig.columnExpanded);
    }

    return hidden;
  }, [hiddenColumns, treeDataConfig]);

  const getUsersColumnConfig = useCallback(
    (col: TableListEditableNewProps<RowData>['columns'][number], columnName: string) => {
      if (col.usersSelection) {
        return { name: columnName, ...col.usersSelection };
      }

      return usersColumnsContent.find((config) => config.name === columnName);
    },
    [usersColumnsContent],
  );

  const getInputColumnConfig = useCallback(
    (col: TableListEditableNewProps<RowData>['columns'][number]) => {
      return col.inputText ?? undefined;
    },
    [],
  );

  const isUsersColumn = useCallback(
    (col: TableListEditableNewProps<RowData>['columns'][number], columnName: string) => (
      usersColumns.includes(columnName) || Boolean(getUsersColumnConfig(col, columnName))
    ),
    [usersColumns, getUsersColumnConfig],
  );

  const isInputColumn = useCallback(
    (col: TableListEditableNewProps<RowData>['columns'][number]) => (
      Boolean(getInputColumnConfig(col))
      || col.cellEditor === CELL_EDITOR_TYPES.LARGE_TEXT
    ),
    [getInputColumnConfig],
  );

  const getCellRenderer = useCallback((col: TableListEditableNewProps<RowData>['columns'][number], columnName: string) => {
    const multiSelection = col.multiSelection !== undefined;
    const userColumn = isUsersColumn(col, columnName);

    if (col.cellRenderer) {
      return { cellRenderer: col.cellRenderer };
    }

    if (multiSelection) {
      return { cellRenderer: buildMultiSelectionCellRenderer(columnName, col.multiSelection as MultiSelectionColumnConfig) };
    }

    if (col.optionsCustom !== undefined) {
      return { cellRenderer: buildOptionsCustomCellRenderer(columnName, col.optionsCustom as OptionsCustomColumnConfig) };
    }

    if (userColumn) {
      return { cellRenderer: buildUsersSelectionCellRenderer(columnName, getUsersColumnConfig(col, columnName) as UsersSelectionColumnConfig | undefined) };
    }

    if (col.cellRendererSelector) {
      return {
        cellRendererSelector: (params: ICellRendererParams<RowData>) =>
          col.cellRendererSelector?.({
            value: params.value,
            data: (params.data ?? {}) as Record<string, unknown>,
          }),
      };
    }

    return {};
  }, [isUsersColumn, getUsersColumnConfig]);

  const inputColumnConfigByName = useMemo(() => {
    const map = new Map<string, NonNullable<TableListEditableNewProps<RowData>['columns'][number]['inputText']>>();
    columns.forEach((col) => {
      if (col.inputText) {
        map.set(col.name, col.inputText);
      }
    });

    return map;
  }, [columns]);

  const columnDefs = useMemo<ColDef<RowData>[]>(
    () =>
      columns.map((col) => {
        const columnName = col.name;
        const inputColumnConfig = getInputColumnConfig(col);
        const isColumnFilteringEnabled = isFilteringEnabledForColumn(columnName);

        const columnFlex = col.flex ?? 1;
        const persistedWidth = columnWidthsRef.current[columnName];
        const columnSize = (() => {
          if (persistedWidth) return { width: persistedWidth };

          if (col.flex !== undefined) return { flex: columnFlex };

          if (col.width) return { width: col.width };

          return { flex: columnFlex };
        })();

        return ({
          field: columnName || undefined,
          headerName: col.title,
          filter: isFiltering && isColumnFilteringEnabled
            ? (col.filterComponent || getColumnFilterType(col))
            : false,
          filterParams: {
            filterOptions: col?.filters || DEFAULT_FILTERS,
            ...(col.valueFormatter ? { valueFormatter: col.valueFormatter } : {}),
            ...(col.filterParams ?? {}),
          },
          ...(col.filterValueGetter
            ? {
              filterValueGetter: (params) => col.filterValueGetter?.({
                value: params.data?.[columnName],
                data: (params.data ?? {}) as Record<string, unknown>,
              }),
            }
            : {}),
          floatingFilter: Boolean(isFiltering && isColumnFilteringEnabled),
          floatingFilterComponentParams: col.floatingFilterComponentParams ?? { filterPlaceholder: t('dialog.filter') },
          ...(col.floatingFilterComponent ? { floatingFilterComponent: col.floatingFilterComponent } : {}),
          hide: resolvedHiddenColumns.has(columnName),
          suppressMovable: col.suppressMovable ?? !allowColumnMove,
          suppressFloatingFilterButton: col.suppressFloatingFilterButton ?? false,
          suppressHeaderFilterButton: col.suppressHeaderFilterButton ?? false,
          suppressHeaderMenuButton: !col.showMenu,
          ...getCellRenderer(col, columnName),
          ...(inputColumnConfig && col.editable !== false ? { editable: true } : {}),
          ...(col.editable ? { editable: col.editable } : {}),
          ...(inputColumnConfig?.maxLength !== undefined && !col.cellEditor
            ? { cellEditorParams: { maxLength: inputColumnConfig.maxLength } }
            : {}),
          ...getCellEditorConfig(col),
          ...(col.cellEditorPopup !== undefined ? { cellEditorPopup: col.cellEditorPopup } : {}),
          ...(col.wrapText ? { wrapText: true, autoHeight: true } : {}),
          ...(col.showMenu && { menuTabs: ['generalMenuTab', 'columnsMenuTab'] }),
          ...(col.align && {
            cellStyle: { justifyContent: ALIGN_TO_JUSTIFY[col.align] },
            cellClass: `ag-cell-align-${col.align}`,
            headerClass: `ag-header-align-${col.align}`,
          }),
          ...(col.valueFormatter ? { valueFormatter: col.valueFormatter } : {}),
          ...(col.pinned ? { pinned: col.pinned } : {}),
          ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
          ...(col.minWidth ? { minWidth: col.minWidth } : {}),
          ...(hasMinWidth && !col.minWidth ? { minWidth: Math.round(columnFlex * 100) } : {}),
          ...(col.headerComponent ? { headerComponent: col.headerComponent } : {}),
          ...(col.headerComponentParams ? { headerComponentParams: col.headerComponentParams } : {}),
          ...columnSize,
          ...(validationStatus && columnId
            ? {
              cellClassRules: {
                [styles.InvalidCell]: (params) => {
                  const rowId = (params.data as Record<string, unknown>)?.[columnId as string];

                  return (
                    !!rowId
                    && !!validationStatus[rowId as string]?.[columnName]
                    && validationStatus[rowId as string][columnName]?.isValid === false
                  );
                },
              },
            }
            : {}),
          ...(col.tooltipValueGetter
            ? {
              tooltipValueGetter: (params) => col.tooltipValueGetter?.({
                value: params.value,
                data: (params.data ?? {}) as Record<string, unknown>,
              }) ?? '',
            }
            : {}),
        } as ColDef<RowData>);
      }),
    [
      columns,
      isFilteringEnabledForColumn,
      isFiltering,
      t,
      resolvedHiddenColumns,
      getCellRenderer,
      getInputColumnConfig,
      hasMinWidth,
      allowColumnMove,
      validationStatus,
      columnId,
    ],
  );

  const getInputConfigForColumn = useCallback((columnName?: string) => {
    if (!columnName) return undefined;

    return inputColumnConfigByName.get(columnName);
  }, [inputColumnConfigByName]);

  const handleCellValueChanged = useCallback((event: CellValueChangedEvent<RowData>) => {
    const columnName = event.colDef.field;

    const inputConfig = getInputConfigForColumn(columnName);

    if (columnName && inputConfig?.onChange && event.data) {
      inputConfig.onChange({ rowData: event.data as Record<string, unknown>, columnName, value: String(event.newValue ?? '') });
    }

    onCellValueChanged?.(event);
  }, [getInputConfigForColumn, onCellValueChanged]);

  const handleCellEditingStopped = useCallback((event: CellEditingStoppedEvent<RowData>) => {
    const columnName = event.colDef.field;
    if (!columnName || !event.data) return;

    const inputConfig = getInputConfigForColumn(columnName);
    if (!inputConfig?.onBlur) return;

    const value = event.data[columnName as keyof RowData];
    inputConfig.onBlur({ rowData: event.data as Record<string, unknown>, columnName, value: String(value ?? '') });
  }, [getInputConfigForColumn]);

  const defaultColDef = useMemo<ColDef<RowData>>(() => ({
    suppressMovable: true,
    filterParams: { maxNumConditions: 1 },
    cellDataType: false,
  }), []);

  const hasWrapText = useMemo(() => columns.some((col) => col.wrapText), [columns]);
  const hasInteractiveInputs = useMemo(() => columns.some((col) => isUsersColumn(col, col.name) || isInputColumn(col)), [columns, isUsersColumn, isInputColumn]);

  const onRowClicked = useCallback((event: RowClickedEvent<RowData>) => event.data?.onClick?.(), []);
  const getRowStyle = useCallback((params: RowClassParams<RowData>) => (params.data?.onClick ? { cursor: 'pointer' } : undefined), []);
  const rowClassRules = useMemo(() => ({
    [styles.RowOverviewSUBCR]: (params: RowClassParams<RowData>) => !!(params.data as Record<string, unknown>)?.isSubCRChildren,
    [styles.RowOverviewCampaignOfSUBCR]: (params: RowClassParams<RowData>) => !!(params.data as Record<string, unknown>)?.thirdLevelRow,
    [styles.RowOverviewBlockingLocation]: (params: RowClassParams<RowData>) => !!(params.data as Record<string, unknown>)?.fourthLevelRow,
    [styles.RowLevelSeparator]: (params: RowClassParams<RowData>) => (
      !!(params.data as Record<string, unknown>)?.isSubCRChildren || !!(params.data as Record<string, unknown>)?.thirdLevelRow
    ),
  }), []);
  const getRowId = useCallback((params: GetRowIdParams<RowData>) => params.data[columnId!] as string, [columnId]);

  const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    if (isSyncingSelectionRef.current || !ACTIONS_TRIGGERED_BY_USER.has(event.source)) return;

    const selectedRows = event.api.getSelectedRows();
    const selectedValues = selectedRows
      .map((row) => row?.[columnId as keyof RowData])
      .filter((value) => value !== undefined) as unknown[];

    onSelectionChange(selectedValues);
  }, [columnId, onSelectionChange]);

  const mapAutoGroupFilterKey = useCallback((model: Record<string, unknown>, toInternal: boolean) => {
    const expandedColumn = treeDataConfig?.columnExpanded;
    if (!expandedColumn) return model;

    const [fromKey, toKey] = toInternal ? [expandedColumn, GROUP_AUTO_COLUMN_ID] : [GROUP_AUTO_COLUMN_ID, expandedColumn];

    if (!(fromKey in model)) return model;

    return Object.fromEntries(Object.entries(model).map(([key, value]) => [key === fromKey ? toKey : key, value]));
  }, [treeDataConfig?.columnExpanded]);

  const onFilterChanged = useCallback((event: FilterChangedEvent<RowData>) => {
    const nextFilterModel = mapAutoGroupFilterKey(event.api.getFilterModel() as Record<string, unknown>, false);
    const serializedFilterModel = serializeComparable(nextFilterModel);

    if (serializedFilterModel === lastEmittedFilterModelRef.current) return;

    lastEmittedFilterModelRef.current = serializedFilterModel;
    onFilterModelChange?.(nextFilterModel);
  }, [mapAutoGroupFilterKey, onFilterModelChange]);

  const onSortChanged = useCallback((event: SortChangedEvent<RowData>) => {
    if (!onSortModelChange) return;

    const sorting = event.api.getColumnState()
      .filter((column) => !!column.sort && !!column.colId)
      .sort((left, right) => (left.sortIndex ?? 0) - (right.sortIndex ?? 0))
      .map((column) => ({
        columnName: column.colId as string,
        direction: column.sort as 'asc' | 'desc',
      }));

    const serializedSorting = serializeComparable(sorting);
    if (serializedSorting === lastEmittedSortModelRef.current) return;

    lastEmittedSortModelRef.current = serializedSorting;

    onSortModelChange(sorting);
  }, [onSortModelChange]);

  const onPaginationChanged = useCallback((event: PaginationChangedEvent<RowData>) => {
    const { api } = event;

    const pageSize = api.paginationGetPageSize();

    if (pageSize !== lastEmittedPageSizeRef.current) {
      lastEmittedPageSizeRef.current = pageSize;
      onPageSizeChange?.(pageSize);
    }

    const currentPage = api.paginationGetCurrentPage();

    if (currentPage !== lastEmittedPageIndexRef.current) {
      lastEmittedPageIndexRef.current = currentPage;
      onPageChange?.(currentPage);
    }
  }, [onPageSizeChange, onPageChange]);

  const onColumnResized = useCallback((event: ColumnResizedEvent<RowData>) => {
    if (!event.finished || !event.column) return;

    let columnId = event.column.getColId();
    const width = event.column.getActualWidth();

    if (treeDataConfig && columnId === GROUP_AUTO_COLUMN_ID) {
      columnId = treeDataConfig.columnExpanded;
    }

    columnWidthsRef.current = { ...columnWidthsRef.current, [columnId]: width };
    onColumnWidthsChange?.(columnWidthsRef.current);
  }, [onColumnWidthsChange, treeDataConfig]);

  const onColumnMoved = useCallback((event: ColumnMovedEvent<RowData>) => {
    if (!onColumnsOrderChanged || !event.finished) return;

    const { api } = event;
    const order = api.getColumnState()
      .filter((column) => !!column.colId)
      .map((column) => column.colId as string);

    onColumnsOrderChanged(order);
  }, [onColumnsOrderChanged]);

  const onColumnVisible = useCallback((event: ColumnVisibleEvent<RowData>) => {
    if (!onColumnVisibilityChanged) return;

    const { api } = event;
    const hidden = api.getColumnState()
      .filter((column) => !!column.colId && column.hide)
      .map((column) => column.colId as string);

    onColumnVisibilityChanged(hidden);
  }, [onColumnVisibilityChanged]);

  const toggleColumnVisibility = useCallback((columnName: string) => {
    const api = gridRef.current?.api;
    const column = api?.getColumn(columnName);
    if (!api || !column) return;

    api.setColumnsVisible([columnName], !column.isVisible());
  }, []);

  const sortedSelection = useMemo(
    () => selection?.map((selectedValue) => sortJsonKeysForComparison(selectedValue)),
    [selection],
  );

  const loadedRowIdsKey = useMemo(() => {
    if (!columnId) return '';

    return list.map((row) => row?.[columnId]).join('|');
  }, [list, columnId]);

  const syncSelectionToGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || !hasCheckbox || !columnId) return;

    const nodesToSelect: IRowNode<RowData>[] = [];
    const nodesToDeselect: IRowNode<RowData>[] = [];

    api.forEachNode((node) => {
      const rowValue = node.data?.[columnId as keyof RowData];
      const shouldSelect = sortedSelection?.some((selectedValue) => selectedValue === sortJsonKeysForComparison(rowValue)) ?? false;
      if (node.isSelected() === shouldSelect) return;
      if (shouldSelect) nodesToSelect.push(node);
      else nodesToDeselect.push(node);
    });

    if (!nodesToSelect.length && !nodesToDeselect.length) return;

    isSyncingSelectionRef.current = true;
    try {
      nodesToDeselect.forEach((node) => node.setSelected(false, false, 'api'));
      nodesToSelect.forEach((node) => node.setSelected(true, false, 'api'));
    } finally {
      isSyncingSelectionRef.current = false;
    }
  }, [columnId, hasCheckbox, sortedSelection]);

  useEffect(() => {
    syncSelectionToGrid();
  }, [syncSelectionToGrid, loadedRowIdsKey]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !expandedRows) return;

    api.forEachNode((node) => {
      const rowId = columnId ? (node.data as Record<string, unknown>)?.[columnId] : node.id;
      const shouldExpand = expandedRows.includes(rowId as string | number);
      if (node.expanded !== shouldExpand) node.setExpanded(shouldExpand);
    });
  }, [expandedRows, columnId]);

  useEffect(() => {
    if (resetFilters === undefined) return;

    const api = gridRef.current?.api;
    if (!api || !gridReady) return;

    api.setFilterModel(null);
  }, [resetFilters, gridReady]);

  useEffect(() => {
    if (filterModel === undefined) return;

    const api = gridRef.current?.api;
    if (!api || !gridReady) return;

    const currentFilterModel = mapAutoGroupFilterKey(api.getFilterModel() as Record<string, unknown>, false);
    if (isSameSerializedValue(currentFilterModel, filterModel)) return;

    lastEmittedFilterModelRef.current = serializeComparable(filterModel);
    api.setFilterModel(mapAutoGroupFilterKey(filterModel, true));
  }, [filterModel, mapAutoGroupFilterKey, gridReady]);

  const handleGridReady = useCallback(() => {
    syncSelectionToGrid();
    setGridReady(true);
  }, [syncSelectionToGrid]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !gridReady) return;

    if (!columnSorting?.length) {
      const sortedColumns = api.getColumnState().filter((column) => column.sort);
      if (!sortedColumns.length) return;

      lastEmittedSortModelRef.current = '';
      api.applyColumnState({ state: [], defaultState: { sort: null } });

      return;
    }

    const target = columnSorting.map<Partial<ColumnState>>((item) => ({
      colId: item.columnName,
      sort: item.direction as 'asc' | 'desc',
    }));
    const targetSerialized = serializeComparable(target);
    if (targetSerialized === lastEmittedSortModelRef.current) return;

    const current = api.getColumnState()
      .filter((column) => column.sort)
      .map<Partial<ColumnState>>((column) => ({ colId: column.colId, sort: column.sort }));
    if (serializeComparable(current) === targetSerialized) return;

    lastEmittedSortModelRef.current = targetSerialized;
    api.applyColumnState({ state: target as unknown as ColumnState[], defaultState: { sort: null } });
  }, [columnSorting, gridReady]);

  return (
    <div className={styles.Root}>
      <Paper className={styles.Paper} elevation={1} style={tableHeight ? { height: tableHeight } : undefined}>
        {showColumnVisibilityButton && (
          <ColumnVisibilityMenu
            columns={columns}
            hiddenColumns={resolvedHiddenColumns}
            excludedColumnName={treeDataConfig?.columnExpanded}
            tooltipText={t('campaignRequests.overviewSettings')}
            onToggleColumn={toggleColumnVisibility}
            onResetColumnsSettings={onResetColumnsSettings}
          />
        )}
        <AgGridReact<RowData>
          ref={gridRef}
          className={`${styles.AgTheme} ${!isPaginated ? styles.AgThemeCompact : ''} ${tableHeight ? styles.AgThemeScrollable : ''}`}
          modules={GRID_MODULES}
          theme={gridTheme}
          rowData={list}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          onGridReady={handleGridReady}
          onRowClicked={onRowClicked}
          getRowStyle={getRowStyle}
          rowClassRules={rowClassRules}
          overlayComponentParams={overlayComponentParams}
          animateRows={animateRows}
          pagination={isPaginated}
          paginationPageSize={paginationPageSizeSelector[0] ?? 10}
          paginationPageSizeSelector={paginationPageSizeSelector}
          headerHeight={40}
          suppressContextMenu
          suppressCellFocus
          singleClickEdit
          tooltipShowDelay={500}
          {...(!hasWrapText && { rowHeight: hasInteractiveInputs ? 52 : 40 })}
          {...(hasCheckbox && { rowSelection })}
          {...(hasCheckbox && { onSelectionChanged })}
          {...(hasCheckbox && { selectionColumnDef })}
          {...(columnId && { getRowId })}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          onPaginationChanged={onPaginationChanged}
          onColumnResized={onColumnResized}
          onColumnMoved={onColumnMoved}
          onColumnVisible={onColumnVisible}
          onCellValueChanged={handleCellValueChanged}
          onCellEditingStopped={handleCellEditingStopped}
          cellSelection
          enableCellTextSelection
          popupParent={popupParent}
          domLayout={tableHeight ? normal : autoHeight}
          {...(isTreeData && { treeData: true })}
          {...(treeDataConfig && { treeDataChildrenField: treeDataConfig.childrenProperty })}
          {...(autoGroupColumnDef && { autoGroupColumnDef })}
        />
      </Paper>
    </div>
  );
}
